// File: /src/components/Administration/Planner/PlannerEventTable.js
import React from "react";
import { Table, Button, Space, Tag, Tooltip, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { AppleOutlined, WindowsOutlined, AndroidOutlined } from '@ant-design/icons';
import { useTheme } from '../../../context/ThemeContext';

const PlannerEventTable = ({
                               data,
                               loading,
                               onEdit,
                               onDelete
                           }) => {
    const { darkTheme } = useTheme();

    const windowsStyle = {
        fontSize: 16,
        marginRight: 8,
        color: darkTheme ? '#60A5FA' : '#1677FF'
    };

    const platformIcons = {
        iOS: <AppleOutlined style={{ fontSize: 16, marginRight: 8 }} />,
        MacCatalyst: <AppleOutlined style={{ fontSize: 16, marginRight: 8 }} />,
        Android: (
            <AndroidOutlined
                style={{
                    fontSize: 16,
                    marginRight: 8,
                    color: darkTheme ? '#A4C639' : '#3DDC84'
                }}
            />
        ),
        Windows: <WindowsOutlined style={windowsStyle} />,
        WinUI: <WindowsOutlined style={windowsStyle} />
    };

    const columns = [
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: 220
        },
        {
            title: "Client",
            dataIndex: ["clientID", "name"],
            key: "client",
            width: 180,
            render: (_, record) => record?.clientID?.name || "-"
        },
        {
            title: "Event",
            dataIndex: "eventID",
            key: "event",
            width: 180,
            render: (_, record) => {
                if (!record?.eventID) return "-";

                const mnemonic = record.eventID?.mnemonic || "";
                const name = record.eventID?.name || "";

                return (
                    <Tooltip title={name}>
                        <span>{mnemonic || name || "-"}</span>
                    </Tooltip>
                );
            }
        },
        {
            title: "Start Date",
            dataIndex: "startDate",
            key: "startDate",
            width: 120,
            render: (value) => value ? dayjs(value).format("DD-MM-YYYY") : "-"
        },
        {
            title: "End Date",
            dataIndex: "endDate",
            key: "endDate",
            width: 120,
            render: (value) => value ? dayjs(value).format("DD-MM-YYYY") : "-"
        },
        {
            title: "Devices",
            dataIndex: "plannedDevices",
            key: "plannedDevices",
            width: 100,
            align: "center",
            render: (value) => Array.isArray(value) ? value.length : 0
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            align: "center",
            render: (status) => {
                const normalized = status || "draft";

                if (normalized === "confirmed") return <Tag color="blue">CONFIRMED</Tag>;
                if (normalized === "completed") return <Tag color="green">COMPLETED</Tag>;
                if (normalized === "cancelled") return <Tag color="red">CANCELLED</Tag>;
                return <Tag color="orange">DRAFT</Tag>;
            }
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            align: "center",
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => onEdit(record)}
                    />

                    <Popconfirm
                        title="Delete planner event"
                        description="Are you sure you want to delete this planner event?"
                        onConfirm={() => onDelete(record)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Table
            bordered
            loading={loading}
            columns={columns}
            dataSource={data}
            rowKey="_id"
            scroll={{ x: "max-content" }}
            expandable={{
                expandedRowRender: (record) => {
                    const devices = record.plannedDevices || [];

                    if (!devices.length) {
                        return <span style={{ color: '#999' }}>No devices assigned</span>;
                    }

                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {devices.map(device => (
                                <div
                                    key={device._id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '4px 0'
                                    }}
                                >
                                    {platformIcons[device.Platform]}
                                    <span>
                                {device.Name || device.SerialNumber || device._id}
                            </span>
                                </div>
                            ))}
                        </div>
                    );
                },
                rowExpandable: (record) =>
                    Array.isArray(record.plannedDevices) &&
                    record.plannedDevices.length > 0
            }}
        />
    );
};

export default PlannerEventTable;